'use client';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useResource } from '@/lib/use-resource';

interface BacktestPoint { date: string; ret: number; cumulative: number; drawdown: number }
interface BacktestData {
  window: { start: string; end: string; days: number };
  finalCumulativeReturn: number;
  maxDrawdown: number;
  maxDrawdownDate: string;
  series: BacktestPoint[];
}

export function DrawdownChart({ dataUrl = '/api/portfolio-snapshot' }: { dataUrl?: string } = {}) {
  const { data: snap } = useResource<{ backtest?: BacktestData }>(dataUrl);
  const data = snap?.backtest ?? null;

  if (!data || !data.series || data.series.length === 0) return null;

  // Decimate to ~120 points so the chart isn't sluggish.
  const stride = Math.max(1, Math.floor(data.series.length / 120));
  const series = data.series.filter((_, i) => i % stride === 0 || i === data.series.length - 1).map(p => ({
    date: p.date,
    cumPct: +(p.cumulative * 100).toFixed(2),
    ddPct: +(p.drawdown * 100).toFixed(2),
  }));

  const fmtTooltip = (v: number, name: string) => {
    if (name === 'cumPct') return [`${v >= 0 ? '+' : ''}${v.toFixed(2)}%`, 'Cumulative'];
    if (name === 'ddPct') return [`${v.toFixed(2)}%`, 'Drawdown'];
    return [String(v), name];
  };

  return (
    <section className="mb-6">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-[13px] font-semibold text-white tracking-wide uppercase">Backtest · 252-day Drawdown</h2>
        <div className="text-[10px] text-[#64748b]">
          {data.window.start} → {data.window.end} · {data.series.length} trading days
        </div>
      </div>
      <div className="bg-[#0d1117] border border-[#1e293b] rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4 mb-4 text-[12px]">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Cumulative return</div>
            <div className={`text-[20px] font-bold tabular-nums ${data.finalCumulativeReturn >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {data.finalCumulativeReturn >= 0 ? '+' : ''}{(data.finalCumulativeReturn * 100).toFixed(1)}%
            </div>
            <div className="text-[10px] text-[#64748b]">last 252 trading days</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Max drawdown</div>
            <div className="text-[20px] font-bold tabular-nums text-red-400">
              {(data.maxDrawdown * 100).toFixed(1)}%
            </div>
            <div className="text-[10px] text-[#64748b]">on {data.maxDrawdownDate}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[#94a3b8]">Calmar (return / |maxDD|)</div>
            <div className="text-[20px] font-bold tabular-nums text-amber-400">
              {data.maxDrawdown < 0
                ? (data.finalCumulativeReturn / Math.abs(data.maxDrawdown)).toFixed(2)
                : '∞'}
            </div>
            <div className="text-[10px] text-[#64748b]">&gt;1 is good, &gt;3 is excellent</div>
          </div>
        </div>

        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="cumFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="ddFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.4} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="#475569" tick={{ fill: '#64748b', fontSize: 10 }} interval={Math.max(0, Math.floor(series.length / 6))} />
              <YAxis
                stroke="#475569"
                tick={{ fill: '#64748b', fontSize: 10 }}
                tickFormatter={(v: number) => `${v.toFixed(0)}%`}
              />
              <Tooltip
                formatter={(v, name) => fmtTooltip(Number(v) || 0, String(name))}
                contentStyle={{ backgroundColor: '#0a0e14', border: '1px solid #334155', borderRadius: 6, fontSize: 12, color: '#e2e8f0' }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
              <Area type="monotone" dataKey="cumPct" stroke="#10b981" strokeWidth={1.5} fill="url(#cumFill)" />
              <Area type="monotone" dataKey="ddPct" stroke="#ef4444" strokeWidth={1.5} fill="url(#ddFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="text-[10px] text-[#64748b] mt-2">
          Green: cumulative return. Red: drawdown from running peak. Holdings + weights frozen at today&apos;s snapshot — survivorship-tilted, but useful for current-book sensitivity.
        </div>
      </div>
    </section>
  );
}
